import { useState, useCallback, useRef } from 'react';
import fastTranslationService from '../services/freeTranslationService';

// Custom hook for free Google Translate
export const useFreeTranslation = () => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationCount, setTranslationCount] = useState(0);
  const abortControllerRef = useRef(null);

  const translateText = useCallback(async (text, targetLanguage, sourceLanguage = 'auto') => {
    if (!text || text.trim().length === 0) {
      return text;
    }

    // Cancel previous request if still running
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setIsTranslating(true);
    
    try {
      const translatedText = await fastTranslationService.translateText(
        text, 
        targetLanguage, 
        sourceLanguage
      );
      
      setTranslationCount(prev => prev + 1);
      return translatedText;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Translation aborted');
        return text;
      }
      console.warn('Translation failed:', error);
      return text; // Return original text on error
    } finally {
      setIsTranslating(false);
    }
  }, []);

  const detectLanguage = useCallback(async (text) => {
    if (!text || text.trim().length === 0) {
      return 'en';
    }

    try {
      return await fastTranslationService.detectLanguage(text);
    } catch (error) {
      console.warn('Language detection failed:', error);
      return 'en';
    }
  }, []);

  const translateBatch = useCallback(async (texts, targetLanguage, sourceLanguage = 'auto') => {
    setIsTranslating(true);
    
    try {
      const results = await fastTranslationService.translateBatch(texts, targetLanguage, sourceLanguage);
      setTranslationCount(prev => prev + texts.length);
      return results;
    } catch (error) {
      console.warn('Batch translation failed:', error);
      return texts; // Return original texts on error
    } finally {
      setIsTranslating(false);
    }
  }, []);

  const clearCache = useCallback(() => {
    fastTranslationService.clearCache();
  }, []);

  const getCacheStats = useCallback(() => {
    return fastTranslationService.getCacheStats();
  }, []);

  // Cleanup on unmount
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return {
    translateText,
    detectLanguage,
    translateBatch,
    isTranslating,
    translationCount,
    clearCache,
    getCacheStats,
    cleanup
  };
};

export default useFreeTranslation;
